import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Scale, Vote, AlertTriangle, CheckCircle2, Layers, Users } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { useProject } from './ProjectLayout'
import {
  Avatar,
  Button,
  Card,
  Chip,
  Tabs,
  cn,
} from '../../design'
import { NewSprintDrawer } from './NewSprintDrawer'
import { SprintActions } from './SprintActions'
import {
  listStories,
  updateStory,
  type Story,
} from '../../api/stories'
import {
  lookupSprints,
  lookupUsers,
  type SprintLookup,
  type UserLookup,
} from '../../api/users'

const FIB_SCALE = [1, 2, 3, 5, 8, 13, 21] as const

/**
 * Phase 8 — Sprint Planning V1+V2+V3+V4 as tabs in one surface.
 *
 * Tabs:
 *   1. Commit — backlog ↔ sprint two-pane w/ capacity bar
 *   2. Capacity — per-user columns; over-allocated cells turn red
 *   3. Poker — pick story; pick estimate from Fibonacci card grid; reveal distribution
 *   4. Balance — visual work-vs-capacity scale
 *
 * Per Atlas handoff README: "wire as tabs or steps inside one Sprint Planning surface".
 */
export default function SprintPlanning() {
  const project = useProject()
  const sprintsQ = useQuery({
    queryKey: ['lookups', 'sprints', project.id],
    queryFn: () => lookupSprints(project.id),
    staleTime: 30_000,
  })

  const sprints = sprintsQ.data ?? []
  // ?sprint=ID (the My Work planner's pinned sprint notes link here) picks the sprint to open.
  const [params] = useSearchParams()
  const wanted = Number(params.get('sprint')) || null
  const initialSprint = sprints.find(s => s.id === wanted) ?? sprints.find(s => s.status === 'active') ?? sprints[0]
  const [sprintId, setSprintId] = useState<number | null>(initialSprint?.id ?? null)
  const [creating, setCreating] = useState(false)
  // A fresh key per open remounts the drawer, so its form and mutation start clean.
  const [drawerKey, setDrawerKey] = useState(0)
  const openCreate = () => { setDrawerKey(k => k + 1); setCreating(true) }
  const hasActiveSprint = sprints.some(s => s.status === 'active')

  // Bind once sprint list loads
  if (!sprintId && initialSprint) {
    setSprintId(initialSprint.id)
  }

  if (sprintsQ.isLoading) {
    return <CenterSpinner />
  }
  if (sprints.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Card>
          <h3 className="text-base font-semibold mb-2">No sprints yet</h3>
          <p className="text-sm text-fg-subtle mb-4">
            Create a sprint to start planning.
          </p>
          <Button leftIcon={<Plus size={14} />} onClick={openCreate}>
            New sprint
          </Button>
        </Card>
        <NewSprintDrawer
          key={drawerKey}
          projectId={project.id}
          open={creating}
          onOpenChange={setCreating}
          hasActiveSprint={hasActiveSprint}
          onCreated={(s) => setSprintId(s.id)}
        />
      </div>
    )
  }

  const sprint = sprints.find(s => s.id === sprintId) ?? sprints[0]!

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-border bg-bg-elevated">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Sprint Planning</h2>
            <p className="text-xs text-fg-subtle">
              Four lenses on the same sprint — commit, balance load, estimate, sanity-check.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SprintPicker sprints={sprints} value={sprint.id} onChange={setSprintId} />
            <SprintActions projectId={project.id} sprint={sprint} onSelect={setSprintId} />
            <Button size="sm" leftIcon={<Plus size={14} />} onClick={openCreate}>
              New sprint
            </Button>
          </div>
        </div>
      </div>

      <NewSprintDrawer
          key={drawerKey}
        projectId={project.id}
        open={creating}
        onOpenChange={setCreating}
        hasActiveSprint={hasActiveSprint}
        onCreated={(s) => setSprintId(s.id)}
      />

      <div className="flex-1 min-h-0 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <Tabs
            items={[
              { value: 'commit',   label: <TabLabel icon={<Layers size={13} />}>Commit</TabLabel>,
                content: <CommitPane sprint={sprint} project={project} /> },
              { value: 'capacity', label: <TabLabel icon={<Users size={13} />}>Capacity</TabLabel>,
                content: <CapacityPane sprint={sprint} project={project} /> },
              { value: 'poker',    label: <TabLabel icon={<Vote size={13} />}>Poker</TabLabel>,
                content: <PokerPane sprint={sprint} project={project} /> },
              { value: 'balance',  label: <TabLabel icon={<Scale size={13} />}>Balance</TabLabel>,
                content: <BalancePane sprint={sprint} project={project} /> },
            ]}
            defaultValue="commit"
          />
        </div>
      </div>
    </div>
  )
}

function TabLabel({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return <span className="inline-flex items-center gap-1.5">{icon}{children}</span>
}

function SprintPicker({ sprints, value, onChange }: { sprints: SprintLookup[]; value: number; onChange: (id: number) => void }) {
  return (
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="rounded-md border border-border bg-bg-elevated px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
    >
      {sprints.map(s => (
        <option key={s.id} value={s.id}>
          {s.name} {s.status === 'active' ? '· active' : s.status === 'closed' ? '· closed' : ''}
        </option>
      ))}
    </select>
  )
}

function CenterSpinner() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="h-6 w-6 rounded-full border-2 border-border border-t-accent animate-spin" />
    </div>
  )
}

// ─── V1: Commit ──────────────────────────────────────────────────────────

function CommitPane({ sprint, project }: { sprint: SprintLookup; project: { id: number; key: string } }) {
  const qc = useQueryClient()
  const storiesQ = useQuery({
    queryKey: ['stories', 'sprint-plan', project.id],
    queryFn: () => listStories({ project_id: project.id, per_page: 200 }),
    refetchInterval: 15_000,
  })

  const all = storiesQ.data?.data ?? []
  const inSprint = all.filter(s => s.sprint_id === sprint.id)
  const backlog  = all.filter(s => s.sprint_id === null && s.status_category !== 'done')

  const totalPts = inSprint.reduce((acc, s) => acc + (s.estimate_points ?? 0), 0)
  const capacity = sprint as unknown as { capacity_points?: number | null }
  // sprint lookup doesn't include capacity; derive from listSprints would be nicer — fallback default
  const cap = capacity.capacity_points ?? 40

  const moveMut = useMutation({
    mutationFn: (vars: { id: number; sprintId: number | null }) =>
      updateStory(vars.id, { sprint_id: vars.sprintId }),
    onMutate: async ({ id, sprintId }) => {
      await qc.cancelQueries({ queryKey: ['stories', 'sprint-plan', project.id] })
      const key = ['stories', 'sprint-plan', project.id]
      const prev = qc.getQueryData<{ data: Story[] }>(key)
      if (prev) {
        qc.setQueryData(key, {
          ...prev,
          data: prev.data.map(s => s.id === id ? { ...s, sprint_id: sprintId } : s),
        })
      }
      return { prev, key }
    },
    onError: (_e, _v, ctx) => { if (ctx) qc.setQueryData(ctx.key, ctx.prev) },
    onSettled: () => qc.invalidateQueries({ queryKey: ['stories'] }),
  })

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-4">
      {/* Backlog */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold tracking-tight">Backlog</h3>
          <p className="text-xs text-fg-subtle mt-0.5">
            {backlog.length} {backlog.length === 1 ? 'story' : 'stories'} unassigned · click → add to sprint
          </p>
        </div>
        <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          {backlog.map(s => (
            <PlanRow
              key={s.id}
              story={s}
              action="add"
              onClick={() => moveMut.mutate({ id: s.id, sprintId: sprint.id })}
            />
          ))}
          {backlog.length === 0 && (
            <li className="px-5 py-6 text-center text-fg-subtle text-sm">Backlog clear.</li>
          )}
        </ul>
      </Card>

      {/* Sprint */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="text-sm font-semibold tracking-tight">{sprint.name}</h3>
            <span className="text-xs font-mono text-fg-subtle">
              {fmtDate(sprint.start_date)} → {fmtDate(sprint.end_date)}
            </span>
          </div>
          <CapacityBar pts={totalPts} cap={cap} />
        </div>
        <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          {inSprint.map(s => (
            <PlanRow
              key={s.id}
              story={s}
              action="remove"
              onClick={() => moveMut.mutate({ id: s.id, sprintId: null })}
            />
          ))}
          {inSprint.length === 0 && (
            <li className="px-5 py-6 text-center text-fg-subtle text-sm">No stories in sprint yet.</li>
          )}
        </ul>
      </Card>
    </div>
  )
}

function PlanRow({
  story,
  action,
  onClick,
}: {
  story: Story
  action: 'add' | 'remove'
  onClick: () => void
}) {
  return (
    <li className="px-5 py-2.5 flex items-center gap-3 hover:bg-bg-muted/50">
      <span className="font-mono text-[11px] text-fg-subtle w-14 shrink-0">{story.key}</span>
      <span className="text-sm flex-1 truncate">{story.title}</span>
      {story.epic_name && (
        <Chip size="sm" tone="epic" color={story.epic_color ?? '#9aa0a6'} dot>
          {story.epic_name}
        </Chip>
      )}
      {story.estimate_points !== null && (
        <Chip size="sm" tone="neutral" className="font-mono">{story.estimate_points}pt</Chip>
      )}
      <button
        onClick={onClick}
        className={cn(
          'p-1 rounded',
          action === 'add'
            ? 'text-accent hover:bg-accent-soft'
            : 'text-fg-subtle hover:text-danger hover:bg-danger/10',
        )}
        title={action === 'add' ? 'Add to sprint' : 'Remove from sprint'}
      >
        {action === 'add' ? <Plus size={14} /> : <X size={14} />}
      </button>
    </li>
  )
}

function CapacityBar({ pts, cap }: { pts: number; cap: number }) {
  const pctOfCap = cap > 0 ? (pts / cap) * 100 : 0
  const over = pts > cap
  const fill = Math.min(100, pctOfCap)
  return (
    <div>
      <div className="relative h-2 rounded-full bg-bg-muted overflow-hidden">
        <div
          className={cn('h-full transition-all', over ? 'bg-danger' : 'bg-accent')}
          style={{ width: `${fill}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-xs mt-1.5">
        <span className={cn('font-mono', over ? 'text-danger font-medium' : 'text-fg-muted')}>
          {pts} / {cap} pt
        </span>
        <span className={cn('text-[10px]', over ? 'text-danger' : 'text-fg-subtle')}>
          {over ? `${Math.round(pctOfCap - 100)}% over` : `${Math.round(100 - pctOfCap)}% headroom`}
        </span>
      </div>
    </div>
  )
}

// ─── V2: Capacity Matrix ─────────────────────────────────────────────────

const DEFAULT_PT_CAP = 12

function CapacityPane({ sprint, project }: { sprint: SprintLookup; project: { id: number; key: string } }) {
  const qc = useQueryClient()
  const usersQ = useQuery({ queryKey: ['lookups', 'users'], queryFn: lookupUsers, staleTime: 60_000 })
  const storiesQ = useQuery({
    queryKey: ['stories', 'sprint-plan', project.id],
    queryFn: () => listStories({ project_id: project.id, per_page: 200 }),
  })

  const users   = usersQ.data ?? []
  const stories = (storiesQ.data?.data ?? []).filter(s => s.sprint_id === sprint.id)
  const unassigned = stories.filter(s => s.assignee_id === null)
  const byUser: Record<number, Story[]> = {}
  for (const s of stories) {
    if (s.assignee_id != null) (byUser[s.assignee_id] ??= []).push(s)
  }
  const unassignedPts = unassigned.reduce((a, s) => a + (s.estimate_points ?? 0), 0)

  const assignMut = useMutation({
    mutationFn: (vars: { id: number; assignee_id: number | null }) =>
      updateStory(vars.id, { assignee_id: vars.assignee_id }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['stories'] }),
  })

  return (
    <div className="space-y-5 mt-4">
      <p className="text-xs text-fg-subtle italic">
        Plan by capacity per person. Use the assignee select on each card to balance load. Default capacity per user is {DEFAULT_PT_CAP}pt — over-allocated cards turn red.
      </p>

      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.max(1, users.length)}, minmax(180px, 1fr))` }}>
        {users.map(u => {
          const myStories = byUser[u.id] ?? []
          const used = myStories.reduce((acc, s) => acc + (s.estimate_points ?? 0), 0)
          const over = used > DEFAULT_PT_CAP
          return (
            <Card key={u.id} padded={false} className={cn(over && 'ring-2 ring-danger/40 bg-danger/5')}>
              <div className="px-3 pt-3 pb-2 border-b border-border">
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} color={u.avatar_color ?? '#9aa0a6'} size="sm" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{u.name}</div>
                    <div className={cn('text-[10px] font-mono', over ? 'text-danger' : 'text-fg-subtle')}>
                      {used} / {DEFAULT_PT_CAP} pt {over && '⚠'}
                    </div>
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-bg-muted overflow-hidden mt-2">
                  <div
                    className={cn('h-full', over ? 'bg-danger' : 'bg-accent')}
                    style={{ width: `${Math.min(100, (used / DEFAULT_PT_CAP) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="p-2 space-y-1.5 min-h-[60px]">
                {myStories.map(s => (
                  <UserCard
                    key={s.id}
                    story={s}
                    users={users}
                    onAssign={(uid) => assignMut.mutate({ id: s.id, assignee_id: uid })}
                  />
                ))}
                {myStories.length === 0 && (
                  <div className="text-[11px] text-fg-disabled text-center py-3 border border-dashed border-border rounded-md">
                    drop here
                  </div>
                )}
              </div>
            </Card>
          )
        })}
      </div>

      <Card padded={false}>
        <div className="px-4 py-3 border-b border-border flex items-baseline justify-between">
          <h3 className="text-sm font-semibold">Unassigned in sprint</h3>
          <span className="text-xs font-mono text-fg-subtle">{unassignedPts} pt</span>
        </div>
        <div className="p-2 space-y-1.5">
          {unassigned.length === 0 && (
            <div className="text-xs text-fg-subtle text-center py-3">All sprint stories assigned.</div>
          )}
          {unassigned.map(s => (
            <UserCard
              key={s.id}
              story={s}
              users={users}
              onAssign={(uid) => assignMut.mutate({ id: s.id, assignee_id: uid })}
            />
          ))}
        </div>
      </Card>
    </div>
  )
}

function UserCard({
  story,
  users,
  onAssign,
}: {
  story: Story
  users: UserLookup[]
  onAssign: (userId: number | null) => void
}) {
  return (
    <div className="rounded-md border border-border bg-bg-elevated px-2.5 py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[10px] text-fg-subtle">{story.key}</span>
        {story.estimate_points !== null && (
          <span className="font-mono text-[10px] text-fg-muted">{story.estimate_points}pt</span>
        )}
      </div>
      <div className="text-[12px] line-clamp-2 mb-1.5">{story.title}</div>
      <select
        value={story.assignee_id ?? ''}
        onChange={e => onAssign(e.target.value === '' ? null : Number(e.target.value))}
        className="w-full text-[11px] rounded border border-border bg-bg px-1.5 py-0.5"
      >
        <option value="">— Unassigned —</option>
        {users.map(u => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>
    </div>
  )
}

// ─── V3: Planning Poker ──────────────────────────────────────────────────

function PokerPane({ sprint, project }: { sprint: SprintLookup; project: { id: number; key: string } }) {
  const qc = useQueryClient()
  const storiesQ = useQuery({
    queryKey: ['stories', 'sprint-plan', project.id],
    queryFn: () => listStories({ project_id: project.id, per_page: 200 }),
  })
  const stories = (storiesQ.data?.data ?? []).filter(s => s.sprint_id === sprint.id)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  // Default to first story without estimate
  const current = stories.find(s => s.key === selectedKey) ??
                  stories.find(s => s.estimate_points === null) ??
                  stories[0]

  const estimateMut = useMutation({
    mutationFn: ({ id, pts }: { id: number; pts: number | null }) =>
      updateStory(id, { estimate_points: pts }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['stories'] }),
  })

  // Distribution histogram across all sprint stories with estimates
  const distribution = useMemo(() => {
    const buckets = new Map<number, number>()
    for (const s of stories) {
      if (s.estimate_points === null) continue
      const v = s.estimate_points as number
      buckets.set(v, (buckets.get(v) ?? 0) + 1)
    }
    return FIB_SCALE.map(p => ({ p, n: buckets.get(p) ?? 0 }))
  }, [stories])
  const maxN = Math.max(1, ...distribution.map(d => d.n))

  if (!current) {
    return <p className="text-sm text-fg-subtle mt-6 text-center">No stories in sprint yet.</p>
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-5 mt-4">
      {/* Estimate now */}
      <Card padded={false}>
        <div className="px-5 py-4 bg-warning/15 border-b border-border">
          <div className="font-mono text-xs text-fg-subtle">{current.key} · estimating now</div>
          <h3 className="text-xl font-semibold tracking-tight mt-1">{current.title}</h3>
          <p className="text-xs text-fg-subtle mt-1">
            Pick a Fibonacci value below to set the estimate. Click a different story on the right to switch.
          </p>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 flex-wrap">
            {FIB_SCALE.map(p => {
              const active = current.estimate_points === p
              return (
                <button
                  key={p}
                  onClick={() => estimateMut.mutate({ id: current.id, pts: p })}
                  className={cn(
                    'h-20 w-14 rounded-md border-2 flex items-center justify-center text-2xl font-semibold transition-all shadow-sm',
                    active
                      ? 'bg-accent text-accent-fg border-accent shadow-md scale-105'
                      : 'bg-bg-elevated border-border hover:border-accent hover:scale-105',
                  )}
                >
                  {p}
                </button>
              )
            })}
            <button
              onClick={() => estimateMut.mutate({ id: current.id, pts: null })}
              className="h-20 w-14 rounded-md border-2 border-dashed border-border bg-bg-muted flex items-center justify-center text-xl text-fg-subtle hover:border-fg-muted"
              title="Clear estimate"
            >
              ?
            </button>
          </div>
          <p className="mt-4 text-xs text-fg-muted">
            Your pick:{' '}
            {current.estimate_points !== null
              ? <span className="text-accent font-medium font-mono">{current.estimate_points} pt ✓</span>
              : <span className="text-fg-subtle italic">none yet</span>}
          </p>
        </div>
      </Card>

      {/* Distribution + queue */}
      <Card padded={false}>
        <div className="px-5 py-4 border-b border-border">
          <h3 className="text-sm font-semibold">Sprint distribution</h3>
          <p className="text-xs text-fg-subtle">Estimates across {stories.length} sprint stories.</p>
        </div>
        <div className="p-5">
          <div className="flex items-end gap-2 h-28 mb-3">
            {distribution.map(b => (
              <div key={b.p} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full bg-accent-soft border border-accent/40 rounded-sm transition-all"
                  style={{ height: `${(b.n / maxN) * 100}%`, minHeight: 4 }}
                  title={`${b.n} stories`}
                />
                <span className="text-[10px] font-mono text-fg-subtle">{b.p}</span>
              </div>
            ))}
          </div>
          <div className="text-xs text-fg-muted">
            <span className="text-accent font-medium">
              total: {stories.reduce((a, s) => a + (s.estimate_points ?? 0), 0)} pt
            </span>
            <span className="text-fg-subtle"> · </span>
            <span>{stories.filter(s => s.estimate_points === null).length} not yet estimated</span>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border">
          <h4 className="text-xs uppercase tracking-wider text-fg-subtle font-semibold mb-2">Sprint queue</h4>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {stories.map(s => (
              <li key={s.id}>
                <button
                  onClick={() => setSelectedKey(s.key)}
                  className={cn(
                    'w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-xs',
                    s.id === current.id ? 'bg-accent-soft' : 'hover:bg-bg-muted',
                  )}
                >
                  <span className="font-mono text-fg-subtle w-12">{s.key}</span>
                  <span className="flex-1 truncate">{s.title}</span>
                  <span className={cn(
                    'font-mono w-8 text-right',
                    s.estimate_points === null ? 'text-fg-disabled' : 'text-fg-muted',
                  )}>
                    {s.estimate_points !== null ? `${s.estimate_points}pt` : '—'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </div>
  )
}

// ─── V4: Balance Scale ───────────────────────────────────────────────────

function BalancePane({ sprint, project }: { sprint: SprintLookup; project: { id: number; key: string } }) {
  const usersQ = useQuery({ queryKey: ['lookups', 'users'], queryFn: lookupUsers, staleTime: 60_000 })
  const storiesQ = useQuery({
    queryKey: ['stories', 'sprint-plan', project.id],
    queryFn: () => listStories({ project_id: project.id, per_page: 200 }),
  })
  const users   = usersQ.data ?? []
  const stories = (storiesQ.data?.data ?? []).filter(s => s.sprint_id === sprint.id)
  const work = stories.reduce((a, s) => a + (s.estimate_points ?? 0), 0)
  const cap  = (sprint as unknown as { capacity_points?: number | null }).capacity_points
              ?? Math.max(users.length * DEFAULT_PT_CAP, 12)
  const headroom = cap - work
  const ratio = work / Math.max(1, cap)
  // Tilt: -8° (work-heavy) ... +8° (cap-heavy). Centered when balanced.
  const tilt = Math.max(-12, Math.min(12, (work - cap) / Math.max(1, cap) * 16))
  const status = work > cap
    ? { kind: 'over', label: 'Over-committed', color: 'danger' as const }
    : headroom <= cap * 0.1
      ? { kind: 'tight', label: 'Tight — minimal headroom', color: 'warning' as const }
      : { kind: 'healthy', label: 'Healthy', color: 'success' as const }

  return (
    <div className="space-y-5 mt-4">
      <Card>
        <div className="flex items-center justify-center pt-4 pb-2">
          <div className="relative w-[600px] max-w-full h-[280px]">
            {/* Beam */}
            <div
              className="absolute left-[8%] right-[8%] h-1.5 bg-fg rounded-full"
              style={{
                bottom: 60,
                transform: `rotate(${tilt}deg)`,
                transformOrigin: 'center',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            {/* Pivot */}
            <div
              className="absolute left-1/2 -translate-x-1/2 w-0 h-0"
              style={{
                bottom: 30,
                borderLeft: '20px solid transparent',
                borderRight: '20px solid transparent',
                borderBottom: '32px solid var(--color-fg)',
              }}
            />
            {/* Ground */}
            <div className="absolute left-0 right-0 h-px bg-border" style={{ bottom: 30 }} />

            {/* Left tray (work) */}
            <div
              className="absolute rounded-xl border-2 border-accent bg-accent-soft p-4 w-52"
              style={{
                left: '8%',
                bottom: 70,
                transform: `rotate(${tilt}deg) translateY(${tilt * 1.5}px)`,
                transformOrigin: 'center top',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="text-[10px] uppercase tracking-wider text-accent-hover font-semibold">Work</div>
              <div className="text-3xl font-semibold tabular-nums tracking-tight">{work}</div>
              <div className="text-xs text-fg-subtle">{stories.length} stories committed</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {stories.map((s, i) => (
                  <span
                    key={i}
                    className="inline-block rounded bg-bg-elevated text-[10px] font-mono px-1.5 py-0.5 border border-border"
                    title={s.title}
                  >
                    {s.estimate_points ?? '?'}
                  </span>
                ))}
              </div>
            </div>

            {/* Right tray (capacity) */}
            <div
              className="absolute rounded-xl border-2 border-success bg-success/10 p-4 w-52"
              style={{
                right: '8%',
                bottom: 70,
                transform: `rotate(${tilt}deg) translateY(${-tilt * 1.5}px)`,
                transformOrigin: 'center top',
                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <div className="text-[10px] uppercase tracking-wider text-success font-semibold">Capacity</div>
              <div className="text-3xl font-semibold tabular-nums tracking-tight">{cap}</div>
              <div className="text-xs text-fg-subtle">
                {users.length} {users.length === 1 ? 'person' : 'people'}
                {users.length > 0 && ` · ${Math.round(cap / users.length)}pt avg`}
              </div>
              <div className="mt-2 flex gap-1">
                {users.slice(0, 6).map(u => (
                  <Avatar key={u.id} size="xs" name={u.name} color={u.avatar_color ?? '#9aa0a6'} />
                ))}
                {users.length > 6 && <span className="text-[10px] text-fg-subtle self-center">+{users.length - 6}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className={cn(
          'mx-auto mt-2 max-w-md rounded-md border-2 px-4 py-3 text-center font-medium',
          status.color === 'danger'  && 'border-danger bg-danger/10 text-danger',
          status.color === 'warning' && 'border-warning bg-warning/15 text-fg',
          status.color === 'success' && 'border-success bg-success/10 text-success',
        )}>
          {status.color === 'success' && <CheckCircle2 size={14} className="inline mr-1.5" />}
          {status.color === 'warning' && <AlertTriangle size={14} className="inline mr-1.5" />}
          {status.color === 'danger'  && <AlertTriangle size={14} className="inline mr-1.5" />}
          {status.label}
          <span className="ml-2 text-xs opacity-75 font-mono">
            {headroom >= 0 ? `+${headroom}pt headroom` : `${headroom}pt over`}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-3 max-w-md mx-auto text-center">
          <Stat label="Stories"  value={stories.length} />
          <Stat label="Avg pt"   value={stories.length > 0 ? (work / stories.length).toFixed(1) : '—'} />
          <Stat label="Load"     value={`${Math.round(ratio * 100)}%`} />
        </div>
      </Card>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-border bg-bg-elevated px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  )
}

// ─── helpers ────────────────────────────────────────────────────────────

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
